import { cn } from '@commenda/utils';
import { Cross2Icon } from '@radix-ui/react-icons';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';

/**
 * Badge variant styles using class-variance-authority (CVA).
 * Defines the visual appearance and behavior of badges across different states.
 */
export const badgeVariants = cva('flex items-center w-fit shrink-0', {
  variants: {
    variant: {
      gray: 'bg-utility-gray-50 text-utility-gray-700',
      error: 'bg-utility-error-50 text-text-error',
      warning: 'bg-utility-warning-50 text-text-warning',
      success: 'bg-bg-success text-text-success',
      blue: 'bg-utility-blue-50 text-text-info',
      orange: 'bg-utility-orange-50 text-utility-orange-700',

      'success-solid': 'bg-bg-success-solid text-white',
      'error-solid': 'bg-bg-error-solid text-white',
      'warning-solid': 'bg-bg-warning-solid text-white',
      'info-solid': 'bg-bg-info-solid text-white',
      'gray-solid': 'bg-bg-gray-solid text-white',
    },
    size: {
      sm: 'h-5 rounded-sm gap-xs py-xxs px-sm txt-extra-small-medium',
      md: 'h-6 rounded-sm gap-sm py-xxs px-md txt-small-medium',
      lg: 'h-7 rounded-md gap-sm py-xs px-[10px] txt-small-medium',
    },
  },
  defaultVariants: {
    variant: 'gray',
    size: 'md',
  },
});

/**
 * Badge component props interface.
 */
interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Avatar image configuration */
  avatar?: {
    url: string;
    alt: string;
  };
  /** Close button configuration */
  closeIcon?: {
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    isShown?: boolean;
  };
  /** Icon to display after the badge content */
  iconTrailing?: React.ReactNode;
  /** Icon to display before the badge content */
  iconLeading?: React.ReactNode;
  /** Whether to show a colored dot indicator */
  hasDot?: boolean;
  /** Per-icon-slot class overrides, merged after the slot defaults so the caller's classes win. Only the leading/trailing icon slots are affected — not the wrapper or children. */
  classNames?: {
    iconLeading?: string;
    iconTrailing?: string;
  };
}

/**
 * Consolidated color mapping for badge elements based on variant.
 */
const BADGE_COLOR_MAP: Record<
  NonNullable<BadgeProps['variant']>,
  {
    dot: string;
    icon: string;
    closeIcon: string;
  }
> = {
  gray: {
    dot: 'bg-utility-gray-700',
    icon: 'text-utility-gray-600',
    closeIcon: 'text-utility-gray-400',
  },
  error: {
    dot: 'bg-utility-error-700',
    icon: 'text-utility-error-600',
    closeIcon: 'text-utility-error-400',
  },
  warning: {
    dot: 'bg-utility-warning-700',
    icon: 'text-utility-warning-600',
    closeIcon: 'text-utility-warning-400',
  },
  success: {
    dot: 'bg-utility-success-700',
    icon: 'text-utility-success-600',
    closeIcon: 'text-utility-success-400',
  },
  blue: {
    dot: 'bg-utility-blue-700',
    icon: 'text-utility-blue-600',
    closeIcon: 'text-utility-blue-400',
  },
  orange: {
    dot: 'bg-utility-orange-700',
    icon: 'text-utility-orange-600',
    closeIcon: 'text-utility-orange-400',
  },
  'success-solid': {
    dot: 'bg-white',
    icon: 'text-white',
    closeIcon: 'text-white',
  },
  'error-solid': {
    dot: 'bg-white',
    icon: 'text-white',
    closeIcon: 'text-white',
  },
  'warning-solid': {
    dot: 'bg-white',
    icon: 'text-white',
    closeIcon: 'text-white',
  },
  'info-solid': {
    dot: 'bg-white',
    icon: 'text-white',
    closeIcon: 'text-white',
  },
  'gray-solid': {
    dot: 'bg-white',
    icon: 'text-white',
    closeIcon: 'text-white',
  },
};

/**
 * Avatar size mapping based on badge size.
 */
const AVATAR_SIZE_MAP: Record<NonNullable<BadgeProps['size']>, string> = {
  sm: 'w-3 h-3',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

/**
 * A versatile badge component that supports multiple variants, sizes, and features.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Badge>Default Badge</Badge>
 *
 * // With variant and size
 * <Badge variant="success" size="lg">Success Badge</Badge>
 *
 * // With close button
 * <Badge closeIcon={{ onClose: handleClose, isShown: true }}>
 *   Closable Badge
 * </Badge>
 *
 * // With avatar and dot
 * <Badge
 *   variant="blue"
 *   avatar={{ url: "/avatar.jpg", alt: "User" }}
 *   hasDot
 * >
 *   User Badge
 * </Badge>
 * ```
 *
 * @param props - Badge configuration properties
 * @param props.variant - Visual style variant (default: 'gray')
 * @param props.size - Size variant (default: 'md')
 * @param props.avatar - Avatar image configuration
 * @param props.closeIcon - Close button configuration
 * @param props.iconTrailing - Icon to display after content
 * @param props.iconLeading - Icon to display before content
 * @param props.hasDot - Whether to show a colored dot indicator
 * @param props.className - Additional CSS classes
 * @param props.children - Badge content
 * @param ref - Forwarded ref to the badge element
 *
 * @returns A badge element with the specified configuration
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant = 'gray',
      size = 'md',
      avatar,
      closeIcon,
      iconTrailing,
      iconLeading,
      hasDot,
      classNames,
      children,
      ...props
    },
    ref
  ) => {
    const colors = BADGE_COLOR_MAP[variant || 'gray'];
    const avatarSize = AVATAR_SIZE_MAP[size || 'md'];

    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {hasDot && (
          <span
            className={cn(
              'inline-block rounded-full shrink-0',
              size === 'sm'
                ? 'h-1 w-1'
                : size === 'md'
                  ? 'h-1.5 w-1.5'
                  : 'h-2 w-2',
              colors.dot
            )}
          />
        )}

        {avatar && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatar.url}
            alt={avatar.alt}
            className={cn('rounded-xs object-cover', avatarSize)}
            onError={(e) => {
              // Fallback to a placeholder if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        )}

        {iconLeading && (
          <span className={cn('shrink-0', colors.icon, classNames?.iconLeading)}>
            {iconLeading}
          </span>
        )}

        {children && <span className="shrink-0">{children}</span>}

        {iconTrailing && (
          <span className={cn('shrink-0', colors.icon, classNames?.iconTrailing)}>
            {iconTrailing}
          </span>
        )}

        {closeIcon?.isShown && (
          <button
            type="button"
            onClick={closeIcon.onClose}
            className={cn(
              'focus:outline-none focus:ring-2 focus:ring-offset-1 rounded-sm',
              'hover:bg-black/5 active:bg-black/10 transition-colors',
              'shrink-0',
              size === 'sm' ? 'p-0.5' : size === 'md' ? 'p-0.5' : 'p-1'
            )}
            aria-label="Remove badge"
          >
            <Cross2Icon
              className={cn(
                size === 'sm'
                  ? 'h-2.5 w-2.5'
                  : size === 'md'
                    ? 'h-3 w-3'
                    : 'h-3.5 w-3.5',
                colors.closeIcon
              )}
            />
          </button>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge, type BadgeProps };
